import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateService } from '@ngx-translate/core';
import { Claims } from '@plantefeve/common/constants/enums/claims.enum';
import { Roles } from '@plantefeve/common/constants/enums/roles.enum';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { PortalContextService } from '../../modules/portal-context/portal-context.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationsService {
  private readonly history: string[] = [];
  private readonly _translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly authService = inject(AuthService);
  private readonly _portalContextService = inject(PortalContextService);

  public back(): void {
    this.history.pop();
    if (this.history.length > 0) {
      this.location.back();
    } else {
      this.router.navigateByUrl('/');
    }
  }

  public getNavigation(): Observable<MenuItem[]> {
    return this.isPortalUser().pipe(
      // Only a portal user may ask which customers the account is linked to; when that fails, keep the menu.
      switchMap((isPortalUser) =>
        isPortalUser ? this._portalContextService.isLinked().pipe(catchError(() => of(true))) : of(false)
      ),
      map((showMenu: boolean) => {
        const menu: MenuItem[] = [];
        if (!showMenu) {
          return menu;
        }

        menu.push({
          items: [
            {
              label: this._translateService.instant('orders'),
              icon: PrimeIcons.TRUCK,
              routerLink: 'orders',
            },
            {
              label: this._translateService.instant('invoices'),
              icon: PrimeIcons.FILE,
              routerLink: 'invoices',
            },
          ],
        });

        return menu;
      })
    );
  }

  public isPortalUser(): Observable<boolean> {
    return this.authService.user$.pipe(
      map((user) => {
        if (user?.[Claims.RoleIdentityClaim]) {
          return user[Claims.RoleIdentityClaim].some((role: string) => role === Roles.PortalUser);
        }
        return false;
      })
    );
  }
}
